/**
 * Resized, re-encoded images for pictures that live in Supabase Storage.
 *
 * Blog banners are stored as full-size PNGs (500 to 600 KB each), which made
 * /blog download close to 50 MB on a phone. On the deployed site they go
 * through Netlify Image CDN instead: resized to the width they are shown at and
 * served as AVIF or WebP by content negotiation, cached at the edge. The source
 * allowlist is `[images] remote_images` in netlify.toml.
 *
 * Locally (vite dev or preview) there is no Image CDN, so the original URL is
 * returned unchanged.
 */
const STORAGE_PUBLIC = /^https:\/\/scluxxccawjbmeelfdwr\.supabase\.co\/storage\/v1\/object\/public\//;

function onNetlify(): boolean {
  // The build-time prerender (a production server build) writes pages that are
  // served from eyecaptain.io, so they use the CDN URLs the browser will use.
  // Anything else would leave a 500 KB original in the prerendered <img>,
  // since React keeps the server's attributes when it hydrates.
  if (typeof window === "undefined") return import.meta.env.PROD;
  return /(^|\.)eyecaptain\.io$|\.netlify\.app$/.test(window.location.hostname);
}

export function cdnImage(url: string | null | undefined, width: number, quality = 70): string {
  if (!url) return "";
  if (!onNetlify() || !STORAGE_PUBLIC.test(url)) return url;
  return `/.netlify/images?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
}

/** A srcset of the given widths, or undefined when the image is not served through the CDN. */
export function cdnSrcSet(url: string | null | undefined, widths: number[], quality = 70): string | undefined {
  if (!url || !onNetlify() || !STORAGE_PUBLIC.test(url)) return undefined;
  return widths.map((w) => `${cdnImage(url, w, quality)} ${w}w`).join(", ");
}
